import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2SearchInput,
  V2Button,
  V2EmptyState,
  V2FormGrid,
  V2FormField,
  Icon,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { faDigits } from '../../lib/faDigits.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { fetchNextAccountCode, type AccountCodeSuggestion } from '../../lib/suggestedCode.js';
import { amountColumnHeader } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  AccountListItem,
  AccountListResponse,
  AccountNormalBalance,
} from '../../lib/types/finance.js';
import { useCoaBalances } from './CoaBalances.js';
import { buildTree, expandableIds, flattenTree, LEVEL_LABELS, matchesFilter } from './CoaTree.js';
import { CoaLevelLegend, CoaTreeGrid } from './CoaTreeGrid.js';

import {
  activeCoaCreateLevels,
  coaCreateSelectionError,
  coaLevelCanPost,
  coaParentIsEligible,
  nextCoaCreateSequence,
} from './coaCreateLevels.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html, frame `/t/kimia/finance` →
 *   «کدینگ حساب‌ها» card (docs/design-system/UI_REFERENCE.md "Finance
 *   language"): tinted section header with meta line + «ورود از اکسل» /
 *   «حساب جدید», «نمایش تا سطح» segmented level tabs, «باز کردن همه» /
 *   «بستن همه», indented tree grid (حساب / سطح / ماهیت / مانده بدهکار /
 *   مانده بستانکار) and a sticky «جمع مانده‌ها — تراز آزمایشی» row with the
 *   «تراز است» badge.
 * canonical pattern: ENTITY_LIST (tree variant) — CoaTreeGrid.tsx
 * desktop reference: v2 finance frame (above)
 * mobile reference: none in the v2 frame — the grid scrolls horizontally
 *   inside `.sk-table-scroll`; the <900px shell is untouched
 * light/dark reference: tokens only (`--sk-mod-*`, `--sk-*`), no hard-coded themes
 * state reference: ready · loading · empty · filtered-empty ·
 *   permission_denied · network_error (balances: loading / unavailable)
 * status: PARTIAL_REAL
 *   - Balances and the TB-totals row are REAL, not derived client-side:
 *     `GET .../finance/reports/trial-balance-columns` (reports.controller.ts:287,
 *     posted-only, up to today). Group/کل rows use the server's own level
 *     rollup (`?levelSequence=1..3`, ledger.ts `target()`); see CoaBalances.ts.
 *     If that report is unavailable (e.g. no journal-read permission) the
 *     balance cells show «—» and the totals row is hidden — never invented.
 *   - Level names (گروه/کل/معین/تفصیلی) are applied to real tree depth
 *     derived from `parentAccountId`; `levelId` is an opaque UUID with no
 *     client lookup, so it is never shown.
 *   - KIT_CONFORMANT: the reference's digit counts («۱ رقم … ۶ رقم») and
 *     the per-row «path» are omitted: code length per level is a template
 *     convention (default-template.ts), not a backend-enforced field, and
 *     no `path` field is serialized by `GET .../finance/accounts`.
 *   - Extra over the reference (kept, real): per-row «قابل ثبت» toggle
 *     (`PATCH .../finance/accounts/:code`), «نمایش غیرفعال‌ها», the create
 *     form behind «حساب جدید» (`POST .../finance/accounts`) and the
 *     default-template action in the empty state — apps/api/src/finance/
 *     accounts.controller.ts. Both mutations address the account by its
 *     **code**, never `accountId`.
 *   - «ورود از اکسل» links to the central `/data` import route
 *     (coa-account import is real — ImportExportPage.tsx), not a page button.
 *
 * Each row links to `AccountDetailPage.tsx` (ERP-199).
 */
export function AccountsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  /** null = default view (top-level groups open); materialised on first toggle. */
  const [expandedState, setExpandedState] = useState<Set<string> | null>(null);
  const [depthLimit, setDepthLimit] = useState<number>(LEVEL_LABELS.length);
  const [showCreate, setShowCreate] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { unit } = useAmountDisplay();
  const balances = useCoaBalances(slug, base, refreshKey);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [levelSequence, setLevelSequence] = useState<number | null>(null);
  const [parentRef, setParentRef] = useState('');
  /** Account type = the root group (دارایی، بدهی، …); it scopes the parent list. */
  const [typeRef, setTypeRef] = useState('');
  const [suggestion, setSuggestion] = useState<AccountCodeSuggestion | null>(null);
  const [isPostable, setIsPostable] = useState(false);
  const suggestionVersion = useRef(0);
  const [suggestionNonce, setSuggestionNonce] = useState(0);
  const [description, setDescription] = useState('');
  /** '' = inherit the parent's nature (server default). */
  const [normalBalance, setNormalBalance] = useState<AccountNormalBalance | ''>('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ code?: string; name?: string }>({});
  const [applyingDefault, setApplyingDefault] = useState(false);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [postableBusyRef, setPostableBusyRef] = useState<string | null>(null);

  const result = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
    refreshKey,
  ]);

  const levelsResult = useApiGet<unknown>(
    slug && showCreate ? `${base}/api/v1/finance/accounts/levels` : null,
    [slug, showCreate],
  );
  const levelData = levelsResult.status === 'ready' ? levelsResult.data : null;
  const activeLevels = useMemo(() => activeCoaCreateLevels(levelData), [levelData]);
  const readyLevels = levelsResult.status === 'ready' ? activeLevels : null;
  const accounts = result.status === 'ready' ? result.data.items : null;
  const selectionError = coaCreateSelectionError(
    readyLevels,
    accounts,
    levelSequence,
    parentRef,
    isPostable,
  );
  const canPost = readyLevels !== null && coaLevelCanPost(readyLevels, levelSequence);

  useEffect(() => {
    if (!showCreate || !readyLevels || levelSequence !== null) return;
    const firstSequence = nextCoaCreateSequence(readyLevels);
    setLevelSequence(firstSequence);
    setIsPostable(coaLevelCanPost(readyLevels, firstSequence));
  }, [showCreate, readyLevels, levelSequence]);

  // Only the latest unchanged selection may accept an asynchronous code suggestion.
  // The server's suggestion never changes the user's selected level or parent.
  useEffect(() => {
    if (!showCreate || selectionError || levelSequence === null) return;
    const version = ++suggestionVersion.current;
    let cancelled = false;
    setSuggestion(null);
    void fetchNextAccountCode(base, parentRef, levelSequence).then((next) => {
      if (
        cancelled ||
        version !== suggestionVersion.current ||
        !next ||
        next.levelSequence !== levelSequence
      )
        return;
      setSuggestion(next);
      setCode(next.code);
      setFieldErrors((current) => {
        const rest = { ...current };
        delete rest.code;
        return rest;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [base, showCreate, parentRef, levelSequence, selectionError, suggestionNonce, levelData]);

  const parentOptions = useMemo(() => {
    if (result.status !== 'ready') return [];
    return [...result.data.items]
      .filter((a) => a.status === 'active')
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [result]);

  const accountsById = useMemo(
    () =>
      new Map((result.status === 'ready' ? result.data.items : []).map((a) => [a.accountId, a])),
    [result],
  );
  const rootCodeOf = (account: AccountListItem): string => {
    let current = account;
    for (let guard = 0; guard < 12 && current.parentAccountId; guard += 1) {
      const up = accountsById.get(current.parentAccountId);
      if (!up) break;
      current = up;
    }
    return current.code;
  };
  const typeOptions = parentOptions.filter(
    (account) =>
      account.parentAccountId === null &&
      readyLevels &&
      nextCoaCreateSequence(readyLevels, account) !== null,
  );
  const parentChoices = parentOptions.filter(
    (account) =>
      readyLevels &&
      levelSequence !== null &&
      coaParentIsEligible(account, readyLevels, levelSequence) &&
      (typeRef === '' || rootCodeOf(account) === typeRef),
  );

  function changeSelection(parent: string, sequence: number | null) {
    suggestionVersion.current += 1;
    setSuggestionNonce((nonce) => nonce + 1);
    setSuggestion(null);
    setCode('');
    setParentRef(parent);
    setLevelSequence(sequence);
    setIsPostable(readyLevels !== null && coaLevelCanPost(readyLevels, sequence));
    setCreateError(null);
  }
  function onTypeChange(ref: string) {
    setTypeRef(ref);
    const parent = parentOptions.find((account) => account.code === ref);
    changeSelection(ref, readyLevels ? nextCoaCreateSequence(readyLevels, parent) : null);
  }
  function onParentChange(ref: string) {
    const parent = parentOptions.find((account) => account.code === ref);
    const sequence =
      readyLevels &&
      parent &&
      levelSequence !== null &&
      coaParentIsEligible(parent, readyLevels, levelSequence)
        ? levelSequence
        : readyLevels
          ? nextCoaCreateSequence(readyLevels, parent)
          : null;
    if (!ref) setTypeRef('');
    changeSelection(ref, sequence);
  }
  function onLevelChange(sequence: number) {
    const parent = parentOptions.find((account) => account.code === parentRef);
    const keepParent = readyLevels && parent && coaParentIsEligible(parent, readyLevels, sequence);
    if (!keepParent) setTypeRef('');
    changeSelection(keepParent ? parentRef : '', sequence);
  }
  function reloadCreateMetadata() {
    suggestionVersion.current += 1;
    levelsResult.reload();
    result.reload();
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setCreateError(null);
    if (selectionError || levelSequence === null) {
      setCreateError(selectionError ?? 'یک سطح فعال انتخاب کنید.');
      return;
    }
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    const nextFieldErrors: { code?: string; name?: string } = {};
    if (!trimmedCode) nextFieldErrors.code = 'کد حساب را وارد کنید.';
    if (!trimmedName) nextFieldErrors.name = 'نام حساب را وارد کنید.';
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    setFieldErrors({});
    setCreating(true);
    try {
      await apiPost(`${base}/api/v1/finance/accounts`, {
        code: trimmedCode,
        name: trimmedName,
        levelSequence,
        parentRef: parentRef || undefined,
        isPostable,
        description: description.trim() || undefined,
        ...(normalBalance ? { normalBalance } : {}),
      });
      setNormalBalance('');
      setName('');
      setIsPostable(coaLevelCanPost(readyLevels ?? [], levelSequence));
      setDescription('');
      setRefreshKey((k) => k + 1);
      // Keep type and parent so the next sibling continues the numbering.
      setSuggestionNonce((nonce) => nonce + 1);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409)
        setSuggestionNonce((nonce) => nonce + 1);
      if (error instanceof ApiError && error.status === 422) reloadCreateMetadata();
      setCreateError(
        bannerMessageFrom(error, 'ثبت حساب ناموفق بود — کد تکراری یا حساب والد نامعتبر است.'),
      );
    } finally {
      setCreating(false);
    }
  }

  async function onApplyDefaultTemplate() {
    setDefaultError(null);
    setApplyingDefault(true);
    try {
      await apiPost(`${base}/api/v1/finance/accounts/default-template`, {});
      setRefreshKey((k) => k + 1);
    } catch (error) {
      setDefaultError(bannerMessageFrom(error, 'اعمال کدینگ پیش‌فرض ناموفق بود.'));
    } finally {
      setApplyingDefault(false);
    }
  }

  async function onTogglePostable(row: AccountListItem) {
    setPostableBusyRef(row.code);
    try {
      await apiPatch(`${base}/api/v1/finance/accounts/${encodeURIComponent(row.code)}`, {
        isPostable: !row.isPostable,
      });
      setRefreshKey((k) => k + 1);
    } finally {
      setPostableBusyRef(null);
    }
  }

  const tree = useMemo(
    () => (result.status === 'ready' ? buildTree(result.data.items) : []),
    [result],
  );
  const codeByAccountId = useMemo(
    () =>
      new Map(result.status === 'ready' ? result.data.items.map((i) => [i.accountId, i.code]) : []),
    [result],
  );
  const searching = query.trim().length > 0;
  const expanded = useMemo(() => expandedState ?? expandableIds(tree, 1), [expandedState, tree]);
  const rows = useMemo(() => {
    if (result.status !== 'ready') return [];
    // Auto-expand every group (all levels) when filtering by text, so a match
    // under a collapsed parent is still reachable.
    const effectiveExpanded = searching ? expandableIds(tree) : expanded;
    const limit = searching ? LEVEL_LABELS.length : depthLimit;
    return flattenTree(tree, effectiveExpanded, limit).filter((n) =>
      matchesFilter(n, query, showInactive),
    );
  }, [result, tree, searching, expanded, depthLimit, query, showInactive]);

  const meta = useMemo(() => {
    if (result.status !== 'ready') return '';
    const items = result.data.items;
    const postable = items.filter((i) => i.isPostable).length;
    return `${faDigits(items.length)} حساب · ${faDigits(postable)} قابل ثبت سند · ${faDigits(
      LEVEL_LABELS.length,
    )} سطح`;
  }, [result]);

  if (!slug) return null;

  function toggle(accountId: string) {
    setExpandedState((prev) => {
      const next = new Set(prev ?? expanded);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }

  /** «نمایش تا سطح N»: show N levels and open exactly the groups needed to reveal them. */
  function selectDepth(levels: number) {
    setDepthLimit(levels);
    setExpandedState(expandableIds(tree, levels - 1));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {showCreate ? (
        <V2Card>
          <V2CardHeader
            icon="plus"
            title="حساب جدید"
            subtitle="افزودن یک حساب به کدینگ حساب‌ها"
            actions={
              <V2Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  suggestionVersion.current += 1;
                  setShowCreate(false);
                }}
              >
                بستن
              </V2Button>
            }
          />
          <form
            noValidate
            onSubmit={onCreate}
            style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              سطح‌ها از کدینگ تعریف‌شده خوانده می‌شوند. تفصیلی شناور، سطح چهارم حساب نیست؛{' '}
              <Link to={`${base}/finance/dimensions`}>مدیریت تفصیلی‌های شناور</Link>.
            </p>
            {levelsResult.status === 'loading' ? (
              <div role="status">در حال دریافت سطح‌های حساب…</div>
            ) : null}
            {levelsResult.status === 'error' ||
            (levelsResult.status === 'ready' && !activeLevels) ? (
              <div role="alert">
                سطح‌های فعال حساب قابل دریافت نیست. برای ایجاد حساب، اطلاعات سطح‌ها را دوباره
                بارگذاری کنید.
                <V2Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={reloadCreateMetadata}
                >
                  بارگذاری دوباره سطح‌ها و حساب‌ها
                </V2Button>
              </div>
            ) : null}
            <V2FormGrid>
              <V2FormField
                label="نوع حساب"
                width="medium"
                hint="گروه اصلی حساب؛ حساب والد و کد بر اساس آن پیشنهاد می‌شود"
              >
                <select
                  className="sk-select"
                  value={typeRef}
                  disabled={!readyLevels || creating}
                  onChange={(e) => onTypeChange(e.target.value)}
                >
                  <option value="">— حساب ریشه (گروه جدید) —</option>
                  {typeOptions.map((a) => (
                    <option key={a.accountId} value={a.code}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField
                label="کد حساب"
                required
                width="short"
                error={fieldErrors.code}
                hint={
                  suggestion
                    ? suggestion.conventionDetected
                      ? 'پیشنهاد بر اساس کد والد؛ قابل تغییر'
                      : 'این شاخه کدگذاری بدون پیشوند والد دارد؛ پیشنهاد در همان الگو است'
                    : undefined
                }
              >
                <input
                  className="sk-input ltr"
                  value={code}
                  onChange={(e) => {
                    suggestionVersion.current += 1;
                    setCode(e.target.value);
                    if (fieldErrors.code)
                      setFieldErrors((f) => {
                        const next = { ...f };
                        delete next.code;
                        return next;
                      });
                  }}
                />
              </V2FormField>
              <V2FormField label="نام حساب" required width="wide" error={fieldErrors.name}>
                <input
                  className="sk-input"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (fieldErrors.name)
                      setFieldErrors((f) => {
                        const next = { ...f };
                        delete next.name;
                        return next;
                      });
                  }}
                />
              </V2FormField>
              <V2FormField label="سطح" required width="medium">
                <select
                  className="sk-select"
                  value={levelSequence ?? ''}
                  disabled={!readyLevels || creating}
                  onChange={(e) => onLevelChange(Number(e.target.value))}
                >
                  <option value="" disabled>
                    انتخاب سطح فعال
                  </option>
                  {(readyLevels ?? []).map((level) => (
                    <option key={level.levelId} value={level.sequence}>
                      {level.displayName} (سطح {faDigits(level.sequence)})
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField
                label="حساب والد"
                width="wide"
                hint="والد باید فعال، غیرقابل ثبت سند و از سطحی پیش از سطح انتخاب‌شده باشد؛ بدون والد فقط اولین سطح فعال ساخته می‌شود"
              >
                <select
                  className="sk-select"
                  value={parentRef}
                  disabled={!readyLevels || creating}
                  onChange={(e) => onParentChange(e.target.value)}
                >
                  <option value="">— بدون والد —</option>
                  {parentChoices.map((a) => (
                    <option key={a.accountId} value={a.code}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField label="شرح" width="wide">
                <input
                  className="sk-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </V2FormField>
              <V2FormField
                label="ماهیت حساب"
                width="medium"
                hint="در تراز، مانده‌ی خلاف ماهیت علامت‌گذاری می‌شود"
              >
                <select
                  className="sk-select"
                  value={normalBalance}
                  onChange={(e) => setNormalBalance(e.target.value as AccountNormalBalance | '')}
                >
                  <option value="">همانند حساب والد</option>
                  <option value="debit">بدهکاربس</option>
                  <option value="credit">بستانکاربس</option>
                  <option value="either">مهم نیست</option>
                </select>
              </V2FormField>
              <V2FormField
                label="قابل ثبت سند"
                width="medium"
                hint="فقط آخرین سطح فعال می‌تواند قابل ثبت سند باشد"
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
                  <input
                    type="checkbox"
                    className="sk-check"
                    checked={isPostable}
                    disabled={!canPost || creating}
                    onChange={(e) => setIsPostable(e.target.checked)}
                  />
                  <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                    سند می‌تواند مستقیماً روی این حساب ثبت شود
                  </span>
                </label>
              </V2FormField>
            </V2FormGrid>
            {selectionError && readyLevels ? (
              <div role="status" style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                {selectionError}
                <V2Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={reloadCreateMetadata}
                >
                  بارگذاری دوباره سطح‌ها و حساب‌ها
                </V2Button>
              </div>
            ) : null}
            {createError ? (
              <div role="alert" style={{ fontSize: 13, color: 'var(--sk-danger)' }}>
                {createError}
              </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <V2Button
                type="submit"
                variant="primary"
                icon="plus"
                disabled={creating || selectionError !== null}
              >
                {creating ? 'در حال ثبت…' : 'ایجاد حساب'}
              </V2Button>
            </div>
          </form>
        </V2Card>
      ) : null}

      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            result.error.status === 403
              ? 'دسترسی به کدینگ حساب‌ها ندارید'
              : 'کدینگ حساب‌ها قابل نمایش نیست'
          }
          description={result.error.status === 403 ? undefined : 'دوباره تلاش کنید.'}
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            icon="layers"
            title="کدینگ حساب‌ها"
            subtitle={meta}
            actions={
              <>
                <Link className="sk-btn sk-btn--secondary sk-btn--sm" to={`${base}/data`}>
                  <Icon name="upload" size="sm" />
                  ورود از اکسل
                </Link>
                <V2Button
                  type="button"
                  variant="primary"
                  size="sm"
                  icon="plus"
                  aria-expanded={showCreate}
                  onClick={() => {
                    setShowCreate(true);
                    setSuggestionNonce((nonce) => nonce + 1);
                  }}
                >
                  حساب جدید
                </V2Button>
              </>
            }
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '8px 14px',
              padding: '8px 14px',
              borderBottom: '1px solid var(--sk-divider)',
            }}
          >
            <V2SearchInput
              placeholder="کد یا عنوان حساب"
              value={query}
              onChange={setQuery}
              width={220}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>نمایش تا سطح</span>
              <div
                className="sk-tabs"
                role="tablist"
                aria-label="نمایش تا سطح"
                style={{ padding: 2, maxWidth: '100%', overflowX: 'auto', whiteSpace: 'nowrap' }}
              >
                {LEVEL_LABELS.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    role="tab"
                    className="sk-tab"
                    aria-selected={depthLimit === i + 1}
                    disabled={searching}
                    style={{ height: 26, padding: '0 10px', fontSize: 12 }}
                    onClick={() => selectDepth(i + 1)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <V2Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={searching}
                onClick={() => setExpandedState(expandableIds(tree))}
              >
                باز کردن همه
              </V2Button>
              <V2Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={searching}
                onClick={() => setExpandedState(new Set())}
              >
                بستن همه
              </V2Button>
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 'var(--sk-fs-sm)',
                color: 'var(--sk-fg-muted)',
              }}
            >
              <input
                type="checkbox"
                className="sk-check"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              نمایش غیرفعال‌ها
            </label>
            <CoaLevelLegend />
          </div>

          {balances.status === 'error' ? (
            <div
              role="status"
              style={{
                padding: '8px 14px',
                fontSize: 12,
                color: 'var(--sk-fg-muted)',
                borderBottom: '1px solid var(--sk-divider)',
              }}
            >
              {balances.httpStatus === 403
                ? 'دسترسی به مانده حساب‌ها (گزارش تراز) ندارید؛ فقط ساختار کدینگ نمایش داده می‌شود.'
                : 'مانده حساب‌ها اکنون قابل بارگذاری نیست؛ ساختار کدینگ نمایش داده می‌شود.'}
            </div>
          ) : null}

          {result.data.items.length === 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 20px 20px' }}
            >
              {defaultError ? (
                <div style={{ color: 'var(--sk-danger)', fontSize: 13 }}>{defaultError}</div>
              ) : null}
              <V2EmptyState
                icon="database"
                title="حسابی ثبت نشده"
                action={
                  <V2Button
                    variant="primary"
                    onClick={() => void onApplyDefaultTemplate()}
                    disabled={applyingDefault}
                  >
                    {applyingDefault ? 'در حال اعمال…' : 'استفاده از کدینگ پیش‌فرض'}
                  </V2Button>
                }
              />
            </div>
          ) : rows.length === 0 ? (
            <V2EmptyState icon="search" title="حسابی با این جست‌وجو پیدا نشد" compact />
          ) : (
            <CoaTreeGrid
              rows={rows}
              codeByAccountId={codeByAccountId}
              expanded={searching ? expandableIds(tree) : expanded}
              onToggle={toggle}
              balances={balances}
              unit={unit}
              base={base}
              postableBusyRef={postableBusyRef}
              onTogglePostable={(row) => void onTogglePostable(row)}
            />
          )}
          <div
            style={{
              padding: '8px 14px',
              fontSize: 11.5,
              color: 'var(--sk-fg-muted)',
              borderTop: '1px dashed var(--sk-divider)',
            }}
          >
            {amountColumnHeader('مانده‌ها', unit)} — فقط اسناد قطعی (ثبت‌شده) تا امروز؛ مانده هر
            گروه جمع زیرحساب‌های آن است.
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}
